// @ts-nocheck
import {
  Button,
  ButtonGroup,
  DialogActionTrigger,
  Input,
  Text,
  VStack,
  Textarea,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";

import { SchemaService } from "@/client";
import useCustomToast from "@/hooks/useCustomToast";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../ui/dialog";
import { Field } from "../ui/field";
import type { SimpleSchemaField } from "@/components/Document/DocumentSchema";

interface SaveSchemaForm {
  schema_name: string;
  description: string;
}

interface SaveSchemaProps {
  isOpen: boolean;
  onClose: () => void;
  currentSchemaContent: SimpleSchemaField[];
  onSuccess?: (savedSchemaId?: string) => void;
}

const SaveSchema = ({
  isOpen,
  onClose,
  currentSchemaContent,
  onSuccess,
}: SaveSchemaProps) => {
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SaveSchemaForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      schema_name: "",
      description: "",
    },
  });

  // 다이얼로그가 열릴 때마다 폼 초기화
  const handleOpenChange = ({ open }: { open: boolean }) => {
    if (open) {
      reset({
        schema_name: "",
        description: "",
      });
    } else {
      onClose();
    }
  };

  const mutation = useMutation({
    mutationFn: (formData: SaveSchemaForm) => {
      return SchemaService.createSchema({
        requestBody: {
          ...formData,
          schema_content: currentSchemaContent,
        },
      });
    },
    onSuccess: (data) => {
      showSuccessToast("스키마가 성공적으로 저장되었습니다.");
      reset();
      onClose();
      if (onSuccess) {
        onSuccess(data.id);
      }
    },
    onError: () => {
      showErrorToast("스키마 저장에 실패했습니다.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["schemas"] });
    },
  });

  const onSubmit: SubmitHandler<SaveSchemaForm> = async (data) => {
    mutation.mutate(data);
  };

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>스키마 저장</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>현재 스키마를 저장하세요.</Text>
            <VStack gap={4}>
              <Field
                required
                invalid={!!errors.schema_name}
                errorText={errors.schema_name?.message}
                label="스키마 이름"
              >
                <Input
                  id="schema_name"
                  {...register("schema_name", {
                    required: "스키마 이름은 필수 입력 항목입니다.",
                  })}
                  placeholder="스키마 이름"
                  type="text"
                />
              </Field>

              <Field
                invalid={!!errors.description}
                errorText={errors.description?.message}
                label="스키마 설명"
              >
                <Textarea
                  id="description"
                  {...register("description")}
                  placeholder="스키마 설명"
                  rows={3}
                />
              </Field>
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button
                  variant="subtle"
                  colorPalette="gray"
                  disabled={isSubmitting}
                >
                  취소
                </Button>
              </DialogActionTrigger>
              <Button variant="solid" type="submit" loading={isSubmitting}>
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default SaveSchema;
